/**
 * @file UserProfile.js
 * @version 1.7.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-16
 * @modified 2026-10-28
 *
 * 1.7.0 (2026-10-28, Cam — rep mobile review): Role reads from the badge —
 * "Sales Rep" for a rep (it said "User"), "Administrator", else "User" — and
 * Guided Mode is left out on a rep's phone, where its "hold Shift" advice
 * means nothing.
 *
 * User profile modal with theme, wallpaper, context menu, and dashboard layout customisation
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './UserProfile.css';
import { COLOR_OPTIONS, WALLPAPER_OPTIONS, applyThemeColor, applyWallpaper, applyOverlay } from '../hooks/useUserPreferences';
import { setGuidedPreference } from '../utils/guidedMode';
import { badgeOf } from '../utils/permissions';
import useRepMobile from '../hooks/useRepMobile';
import { useSlideOutButton } from './SlideOutButton';
import CustomIcon from './CustomIcon';
import DashboardConfigurator from './DashboardConfigurator';
import { flattenMenuItems } from '../utils/flattenMenuItems';

function UserProfile({ user, onClose, preferences, onSavePreferences, saving, menuItems, dashboardSettings, onSaveDashboard, onResetDashboard, savingDashboard }) {
  // A rep's phone leaves Guided Mode out (1.7.0).
  const repMobile = useRepMobile();
  // Set by the server for a REP badge (config/repDashboardLayout.js). The
  // configurator is pointless when every write it can make is refused.
  const dashboardLocked = dashboardSettings?.locked === true;
  const [closing, setClosing] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const [isPanelOpening, setIsPanelOpening] = useState(false);
  const prevActivePanelRef = useRef(null);

  // Local state for editing
  const [selectedColor, setSelectedColor] = useState(preferences.theme_color || 'aqua');
  const [selectedWallpaper, setSelectedWallpaper] = useState(preferences.wallpaper || '4046.jpg');
  const [overlayColor, setOverlayColor] = useState(preferences.overlay_color || 'dark');
  const [overlayOpacity, setOverlayOpacity] = useState(preferences.overlay_opacity ?? 0.2);
  const [guidedMode, setGuidedMode] = useState(!!preferences.guided_mode);
  const [contextMenuItems, setContextMenuItems] = useState(() => {
    if (preferences.context_menu_items && preferences.context_menu_items.length > 0) {
      return preferences.context_menu_items;
    }
    return menuItems.map(item => item.id);
  });

  // Favourites nav — stored as a list of typed entries:
  //   { type: 'item', id }, { type: 'section', label }, { type: 'divider' },
  //   { type: 'submenu', id, label, icon, children: [...] }
  const [favouritesItems, setFavouritesItems] = useState(() =>
    Array.isArray(preferences.favourites_items) ? preferences.favourites_items : []
  );
  const [favDirty, setFavDirty] = useState(false);
  const [favSectionDraft, setFavSectionDraft] = useState('');
  const [favSubmenuDraft, setFavSubmenuDraft] = useState({ label: '', icon: 'folder' });
  const [favExpandedSubmenus, setFavExpandedSubmenus] = useState(new Set());
  // Per-submenu "add section" draft label — keyed by submenu id
  const [favChildSectionDrafts, setFavChildSectionDrafts] = useState({});

  // Dashboard configurator ref (for calling save/reset)
  const configuratorRef = useRef();

  // Track dirty state
  const [themeDirty, setThemeDirty] = useState(false);
  const [contextDirty, setContextDirty] = useState(false);

  // Slideout button measurement — re-measure when panel or dirty state changes
  useSlideOutButton([activePanel, themeDirty, contextDirty, favDirty, savingDashboard]);

  // Track panel opening
  useEffect(() => {
    const wasOpen = prevActivePanelRef.current !== null;
    const isOpening = !wasOpen && activePanel !== null;
    setIsPanelOpening(isOpening);
    prevActivePanelRef.current = activePanel;
  }, [activePanel]);

  useEffect(() => {
    document.body.classList.add('modal-open');
    const contentWrapper = document.querySelector('.content-wrapper');
    if (contentWrapper) contentWrapper.classList.add('modal-open');

    return () => {
      document.body.classList.remove('modal-open');
      const cw = document.querySelector('.content-wrapper');
      if (cw) cw.classList.remove('modal-open');
    };
  }, []);

  const handleClose = () => {
    if (themeDirty) {
      applyThemeColor(preferences.theme_color);
      applyWallpaper(preferences.wallpaper);
      applyOverlay(preferences.overlay_color || 'dark', preferences.overlay_opacity ?? 0.2);
    }
    setClosing(true);
    setTimeout(() => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) contentWrapper.classList.remove('modal-open');
      onClose();
    }, 300);
  };

  const handlePanelToggle = (panel) => {
    setActivePanel(activePanel === panel ? null : panel);
  };

  // Theme handlers
  const handleColorSelect = (colorKey) => {
    setSelectedColor(colorKey);
    setThemeDirty(true);
    applyThemeColor(colorKey);
  };

  const handleWallpaperSelect = (wallpaperFile) => {
    setSelectedWallpaper(wallpaperFile);
    setThemeDirty(true);
    applyWallpaper(wallpaperFile);
  };

  const handleOverlayColorChange = (color) => {
    setOverlayColor(color);
    setThemeDirty(true);
    applyOverlay(color, overlayOpacity);
  };

  /*
   * Guided Mode applies the moment it is ticked and saves itself: it lives on
   * the left panel, which has no Save button.
   *
   * The save sends the STORED preferences with guided_mode changed — the
   * endpoint resets any theme field it is not sent to the default. Saving then
   * re-applies the stored theme, so a theme change still being previewed in the
   * Theme panel is put back on screen afterwards rather than lost.
   */
  const handleGuidedModeChange = async (on) => {
    setGuidedMode(on);
    setGuidedPreference(on);
    const result = await onSavePreferences({ ...preferences, guided_mode: on });
    if (!result?.success) {
      setGuidedMode(!on);
      setGuidedPreference(!on);
      return;
    }
    if (themeDirty) {
      applyThemeColor(selectedColor);
      applyWallpaper(selectedWallpaper);
      applyOverlay(overlayColor, overlayOpacity);
    }
  };

  const handleOverlayOpacityChange = (opacity) => {
    const val = parseFloat(opacity);
    setOverlayOpacity(val);
    setThemeDirty(true);
    applyOverlay(overlayColor, val);
  };

  const handleRevertTheme = () => {
    setSelectedColor(preferences.theme_color);
    setSelectedWallpaper(preferences.wallpaper);
    setOverlayColor(preferences.overlay_color || 'dark');
    setOverlayOpacity(preferences.overlay_opacity ?? 0.2);
    applyThemeColor(preferences.theme_color);
    applyWallpaper(preferences.wallpaper);
    applyOverlay(preferences.overlay_color || 'dark', preferences.overlay_opacity ?? 0.2);
    setThemeDirty(false);
  };

  const handleSaveTheme = async () => {
    const result = await onSavePreferences({
      ...preferences,
      theme_color: selectedColor,
      wallpaper: selectedWallpaper,
      overlay_color: overlayColor,
      overlay_opacity: overlayOpacity
    });
    if (result.success) {
      setThemeDirty(false);
    }
  };

  // Context menu handlers
  const handleToggleContextItem = (itemId) => {
    setContextMenuItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
    setContextDirty(true);
  };

  const handleMoveContextItem = (itemId, direction) => {
    setContextMenuItems(prev => {
      const idx = prev.indexOf(itemId);
      if (idx === -1) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const newItems = [...prev];
      [newItems[idx], newItems[newIdx]] = [newItems[newIdx], newItems[idx]];
      return newItems;
    });
    setContextDirty(true);
  };

  const handleResetContextMenu = () => {
    setContextMenuItems(menuItems.map(item => item.id));
    setContextDirty(true);
  };

  const handleRevertContextMenu = () => {
    setContextMenuItems(
      preferences.context_menu_items && preferences.context_menu_items.length > 0
        ? preferences.context_menu_items
        : menuItems.map(item => item.id)
    );
    setContextDirty(false);
  };

  const handleSaveContextMenu = async () => {
    const result = await onSavePreferences({
      ...preferences,
      context_menu_items: contextMenuItems
    });
    if (result.success) {
      setContextDirty(false);
    }
  };

  // ==========================================
  // Favourites handlers
  // ==========================================
  const handleAddFavouriteItem = (itemId) => {
    setFavouritesItems(prev => [...prev, { type: 'item', id: itemId }]);
    setFavDirty(true);
  };

  const handleAddFavouriteSection = () => {
    const label = (favSectionDraft || '').trim();
    if (!label) return;
    setFavouritesItems(prev => [...prev, { type: 'section', label }]);
    setFavSectionDraft('');
    setFavDirty(true);
  };

  const handleAddFavouriteDivider = () => {
    setFavouritesItems(prev => [...prev, { type: 'divider' }]);
    setFavDirty(true);
  };

  const handleAddFavouriteSubmenu = () => {
    const label = (favSubmenuDraft.label || '').trim();
    const icon = (favSubmenuDraft.icon || 'folder').trim() || 'folder';
    if (!label) return;
    const id = `submenu-${Date.now()}`;
    setFavouritesItems(prev => [...prev, { type: 'submenu', id, label, icon, children: [] }]);
    setFavSubmenuDraft({ label: '', icon: 'folder' });
    // Auto-expand the new submenu's editor so the user can immediately add children
    setFavExpandedSubmenus(prev => { const n = new Set(prev); n.add(id); return n; });
    setFavDirty(true);
  };

  const toggleFavSubmenuExpanded = (submenuId) => {
    setFavExpandedSubmenus(prev => {
      const n = new Set(prev);
      if (n.has(submenuId)) n.delete(submenuId); else n.add(submenuId);
      return n;
    });
  };

  // Child management inside a submenu
  const updateSubmenuChildren = (submenuIdx, updater) => {
    setFavouritesItems(prev => prev.map((entry, i) => {
      if (i !== submenuIdx || entry.type !== 'submenu') return entry;
      return { ...entry, children: updater(entry.children || []) };
    }));
    setFavDirty(true);
  };

  const handleAddChildItem = (submenuIdx, itemId) => {
    updateSubmenuChildren(submenuIdx, (kids) => [...kids, { type: 'item', id: itemId }]);
  };

  const handleAddChildDivider = (submenuIdx) => {
    updateSubmenuChildren(submenuIdx, (kids) => [...kids, { type: 'divider' }]);
  };

  const handleAddChildSection = (submenuIdx, label) => {
    const l = (label || '').trim();
    if (!l) return;
    updateSubmenuChildren(submenuIdx, (kids) => [...kids, { type: 'section', label: l }]);
  };

  const handleMoveChild = (submenuIdx, childIdx, direction) => {
    updateSubmenuChildren(submenuIdx, (kids) => {
      const newIdx = childIdx + direction;
      if (newIdx < 0 || newIdx >= kids.length) return kids;
      const copy = [...kids];
      [copy[childIdx], copy[newIdx]] = [copy[newIdx], copy[childIdx]];
      return copy;
    });
  };

  const handleRemoveChild = (submenuIdx, childIdx) => {
    updateSubmenuChildren(submenuIdx, (kids) => kids.filter((_, i) => i !== childIdx));
  };

  const handleUpdateSubmenuMeta = (submenuIdx, field, value) => {
    setFavouritesItems(prev => prev.map((entry, i) => (
      i === submenuIdx && entry.type === 'submenu' ? { ...entry, [field]: value } : entry
    )));
    setFavDirty(true);
  };

  const handleMoveFavourite = (idx, direction) => {
    setFavouritesItems(prev => {
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
      return copy;
    });
    setFavDirty(true);
  };

  const handleRemoveFavourite = (idx) => {
    setFavouritesItems(prev => prev.filter((_, i) => i !== idx));
    setFavDirty(true);
  };

  const handleCopyFromContextMenu = () => {
    // Clone the current context menu configuration as a flat list of items.
    setFavouritesItems(contextMenuItems.map(id => ({ type: 'item', id })));
    setFavDirty(true);
  };

  const handleClearFavourites = () => {
    setFavouritesItems([]);
    setFavDirty(true);
  };

  const handleRevertFavourites = () => {
    setFavouritesItems(Array.isArray(preferences.favourites_items) ? preferences.favourites_items : []);
    setFavDirty(false);
  };

  const handleSaveFavourites = async () => {
    const result = await onSavePreferences({
      ...preferences,
      favourites_items: favouritesItems.length > 0 ? favouritesItems : null
    });
    if (result.success) setFavDirty(false);
  };

  // Context-menu candidates include top-level sections AND their sub-items
  // (e.g. "Search Contacts" under "Contacts"). Headers are skipped.
  const contextMenuCandidates = flattenMenuItems(menuItems);
  const menuItemMap = {};
  contextMenuCandidates.forEach(item => { menuItemMap[item.id] = item; });

  // Items not currently in the context menu (available to add)
  const availableItems = contextMenuCandidates.filter(item => !contextMenuItems.includes(item.id));

  const currentTheme = document.documentElement.getAttribute('data-theme') || 'dark';

  // Determine which dirty state and save/revert to show based on active panel
  const isDirty = activePanel === 'theme' ? themeDirty
    : activePanel === 'context-menu' ? contextDirty
    : activePanel === 'favourites' ? favDirty
    : false;
  const handleSave = activePanel === 'theme' ? handleSaveTheme
    : activePanel === 'context-menu' ? handleSaveContextMenu
    : activePanel === 'favourites' ? handleSaveFavourites
    : () => {};
  const handleRevert = activePanel === 'theme' ? handleRevertTheme
    : activePanel === 'context-menu' ? handleRevertContextMenu
    : activePanel === 'favourites' ? handleRevertFavourites
    : () => {};

  return createPortal(
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`modal-content user-profile-modal ${closing ? 'closing' : ''} ${activePanel ? 'expanded' : ''} ${activePanel === 'dashboard' ? 'dashboard-panel' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left Panel */}
        <div className="profile-panel-left">
          <div className="panel-header">
            <div className="header-title">
              <div className="profile-avatar">
                <span>{(user.first_name?.[0] || '') + (user.last_name?.[0] || '')}</span>
              </div>
              <h2>{user.first_name} {user.last_name}</h2>
            </div>
            <div className="header-actions">
              <button className="btn-icon close-btn" onClick={handleClose}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
          </div>

          <div className="panel-body">
            <div className="info-section">
              <h3>Account Information</h3>
              <div className="info-columns">
                <div className="info-col">
                  <div className="info-item">
                    <span className="info-label">First Name</span>
                    <span className="info-value">{user.first_name || 'Not set'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Last Name</span>
                    <span className="info-value">{user.last_name || 'Not set'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email</span>
                    <span className="info-value">{user.email}</span>
                  </div>
                </div>
                <div className="info-col">
                  <div className="info-item">
                    <span className="info-label">Role</span>
                    <span className="info-value">{user.is_admin || badgeOf(user) === 'ADMIN' ? 'Administrator' : badgeOf(user) === 'REP' ? 'Sales Rep' : 'User'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Theme</span>
                    <span className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span
                        className="color-dot-inline"
                        style={{ background: COLOR_OPTIONS[selectedColor]?.[currentTheme]?.hex || COLOR_OPTIONS.aqua.dark.hex }}
                      />
                      {COLOR_OPTIONS[selectedColor]?.label || 'Aqua'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Wallpaper</span>
                    <span className="info-value">
                      {selectedWallpaper === 'none' ? 'None' : WALLPAPER_OPTIONS.find(w => w.file === selectedWallpaper)?.label || selectedWallpaper}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* How the screen behaves for this user. On the left panel rather
                than inside Theme, where it sat below the whole wallpaper grid
                and was not found. Saves the moment it is ticked — there is no
                Save button out here. */}
            {!repMobile && (
            <div className="info-section guided-mode-setting">
              <h3>Guided Mode</h3>
              <p className="guided-mode-hint">Show every button label and menu tooltip straight away, without the usual pause.</p>
              <div className="guided-mode-row">
                <div className="checkbox-container">
                  <div className="check light">
                    <input
                      id="guided-mode"
                      type="checkbox"
                      checked={guidedMode}
                      onChange={(e) => handleGuidedModeChange(e.target.checked)}
                    />
                    <label htmlFor="guided-mode"></label>
                  </div>
                </div>
                <p className="guided-mode-explainer">
                  Buttons normally wait about a second and a half before telling you what
                  they do, so a row of them doesn't unfurl as your cursor crosses the
                  screen. Guided Mode removes that wait — useful while you are learning a
                  screen, or on one you only use occasionally.
                  <strong>You can get the same thing at any time by holding SHIFT.</strong>
                </p>
              </div>
            </div>
            )}
          </div>

          {/* Panel Navigation Buttons */}
          <div className="panel-nav-buttons">
            <div>
              <button
                className={`btn-slideout ${activePanel === 'theme' ? 'active' : ''}`}
                onClick={() => handlePanelToggle('theme')}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">palette</span>
                </div>
                <span className="btn-label">Theme</span>
              </button>
            </div>
            <div>
              <button
                className={`btn-slideout ${activePanel === 'context-menu' ? 'active' : ''}`}
                onClick={() => handlePanelToggle('context-menu')}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">apps</span>
                </div>
                <span className="btn-label">Context Menu</span>
              </button>
            </div>
            <div>
              <button
                className={`btn-slideout ${activePanel === 'favourites' ? 'active' : ''}`}
                onClick={() => handlePanelToggle('favourites')}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">star</span>
                </div>
                <span className="btn-label">Favourites</span>
              </button>
            </div>
            {/* A rep's layout is fixed in config/repDashboardLayout.js and the
                server refuses every write to it, so the configurator would only
                be able to fail. Disabled with a reason rather than hidden — the
                same courtesy the rest of the app extends to a view-only
                account, and it explains the absence instead of leaving a gap. */}
            <div>
              <button
                className={`btn-slideout ${activePanel === 'dashboard' ? 'active' : ''}${dashboardLocked ? ' btn-disabled' : ''}`}
                onClick={() => !dashboardLocked && handlePanelToggle('dashboard')}
                disabled={dashboardLocked}
                title={dashboardLocked
                  ? 'Your dashboard is set by the office and cannot be rearranged'
                  : undefined}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">
                    {dashboardLocked ? 'lock' : 'dashboard_customize'}
                  </span>
                </div>
                <span className="btn-label">Dashboard</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className={`profile-panel-right ${activePanel ? 'open' : ''}`}>
          {/* Fixed header with title and save/revert slideout buttons */}
          <div className="right-panel-header">
            <h3>
              {activePanel === 'theme' && 'Theme & Wallpaper'}
              {activePanel === 'context-menu' && 'Context Menu'}
              {activePanel === 'favourites' && 'Favourites Nav Menu'}
              {activePanel === 'dashboard' && 'Dashboard Layout'}
            </h3>
            <div className="right-panel-header-actions">
              {isDirty && (
                <>
                  <button className="btn-slideout" onClick={handleSave} disabled={saving}>
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">save</span>
                    </div>
                    <span className="btn-label">{saving ? 'Saving...' : 'Save'}</span>
                  </button>
                  <button className="btn-slideout" onClick={handleRevert}>
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">undo</span>
                    </div>
                    <span className="btn-label">Revert</span>
                  </button>
                </>
              )}
              {activePanel === 'context-menu' && (
                <button className="btn-slideout" onClick={handleResetContextMenu}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">restart_alt</span>
                  </div>
                  <span className="btn-label">Reset</span>
                </button>
              )}
              {activePanel === 'dashboard' && !dashboardLocked && (
                <>
                  <button className="btn-slideout" onClick={() => configuratorRef.current?.save()} disabled={savingDashboard}>
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">save</span>
                    </div>
                    <span className="btn-label">{savingDashboard ? 'Saving...' : 'Save'}</span>
                  </button>
                  <button className="btn-slideout btn-danger" onClick={() => configuratorRef.current?.reset()} disabled={savingDashboard}>
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">restart_alt</span>
                    </div>
                    <span className="btn-label">Reset</span>
                  </button>
                </>
              )}
              <button data-read-safe className="btn-slideout" onClick={() => setActivePanel(null)} title="Collapse panel">
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">chevron_left</span>
                </div>
                <span className="btn-label">Close</span>
              </button>
            </div>
          </div>

          <div className="right-panel-content">
            {/* Theme Panel */}
            {activePanel === 'theme' && (
              <div key="theme" className={`panel-content-section ${isPanelOpening ? 'panel-opening' : ''}`}>
                {/* Colour Picker */}
                <div className="theme-section">
                  <h4>Accent Colour</h4>
                  <p className="theme-hint">Choose your primary accent colour. Both shades are shown: the brighter version is used for dark theme, the deeper version for light theme.</p>
                  <div className="color-grid">
                    {Object.entries(COLOR_OPTIONS).map(([key, color]) => (
                      <button
                        key={key}
                        className={`color-option ${selectedColor === key ? 'selected' : ''}`}
                        onClick={() => handleColorSelect(key)}
                        title={color.label}
                      >
                        <div className="color-swatches">
                          <span className="color-swatch dark" style={{ background: color.dark.hex }} />
                          <span className="color-swatch light" style={{ background: color.light.hex }} />
                        </div>
                        <span className="color-label">{color.label}</span>
                        {selectedColor === key && (
                          <span className="material-symbols-rounded color-check">check_circle</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Wallpaper Picker */}
                <div className="theme-section">
                  <h4>Wallpaper</h4>
                  <p className="theme-hint">Choose a background wallpaper for the main content area.</p>
                  <div className="wallpaper-grid">
                    {WALLPAPER_OPTIONS.map(wp => (
                      <button
                        key={wp.file}
                        className={`wallpaper-option ${selectedWallpaper === wp.file ? 'selected' : ''}`}
                        onClick={() => handleWallpaperSelect(wp.file)}
                        title={wp.label}
                      >
                        {wp.file === 'none' ? (
                          <div className="wallpaper-preview wallpaper-none">
                            <span className="material-symbols-rounded">block</span>
                          </div>
                        ) : (
                          <div
                            className="wallpaper-preview"
                            style={{ backgroundImage: `url(/wallpapers/thumbs/${wp.file})` }}
                          />
                        )}
                        <span className="wallpaper-label">{wp.label}</span>
                        {selectedWallpaper === wp.file && (
                          <span className="material-symbols-rounded wallpaper-check">check_circle</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Overlay Controls */}
                <div className="theme-section">
                  <h4>Wallpaper Overlay</h4>
                  <p className="theme-hint">Control the overlay colour and opacity over the wallpaper. This does not change with light/dark theme switching.</p>

                  <div className="overlay-controls">
                    <div className="overlay-color-toggle">
                      <button
                        className={`overlay-btn ${overlayColor === 'dark' ? 'selected' : ''}`}
                        onClick={() => handleOverlayColorChange('dark')}
                      >
                        <span className="overlay-swatch overlay-dark" />
                        Dark
                      </button>
                      <button
                        className={`overlay-btn ${overlayColor === 'light' ? 'selected' : ''}`}
                        onClick={() => handleOverlayColorChange('light')}
                      >
                        <span className="overlay-swatch overlay-light" />
                        Light
                      </button>
                    </div>

                    <div className="overlay-opacity-control">
                      <label>
                        Opacity
                        <span className="opacity-value">{Math.round(overlayOpacity * 100)}%</span>
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={overlayOpacity}
                        onChange={(e) => handleOverlayOpacityChange(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Dashboard Layout Panel */}
            {activePanel === 'dashboard' && dashboardSettings && !dashboardLocked && (
              <div key="dashboard" className={`panel-content-section ${isPanelOpening ? 'panel-opening' : ''}`}>
                <DashboardConfigurator
                  ref={configuratorRef}
                  embedded
                  currentSettings={dashboardSettings}
                  onSave={onSaveDashboard}
                  onReset={onResetDashboard}
                  saving={savingDashboard}
                  user={user}
                />
              </div>
            )}

            {/* Context Menu Panel */}
            {activePanel === 'context-menu' && (
              <div key="context-menu" className={`panel-content-section ${isPanelOpening ? 'panel-opening' : ''}`}>
                <div className="theme-section">
                  <h4>Menu Items</h4>
                  <p className="theme-hint">Choose which items appear in the right-click context menu and their order. Use arrows to reorder.</p>

                  {/* Active items */}
                  <div className="context-items-list">
                    {contextMenuItems.map((itemId, idx) => {
                      const item = menuItemMap[itemId];
                      if (!item) return null;
                      return (
                        <div key={itemId} className="context-item active">
                          <div className="context-item-icon">
                            {item.isSvg ? (
                              <CustomIcon name={item.icon} size={18} />
                            ) : item.isLetter ? (
                              <span className="letter-icon">{item.icon}</span>
                            ) : (
                              <span className="material-symbols-rounded">{item.icon}</span>
                            )}
                          </div>
                          <span className="context-item-label">
                            {item.parentLabel && <span className="context-item-parent">{item.parentLabel} › </span>}
                            {item.label}
                          </span>
                          <div className="context-item-actions">
                            <button
                              className="btn-icon-sm"
                              onClick={() => handleMoveContextItem(itemId, -1)}
                              disabled={idx === 0}
                              title="Move up"
                            >
                              <span className="material-symbols-rounded">keyboard_arrow_up</span>
                            </button>
                            <button
                              className="btn-icon-sm"
                              onClick={() => handleMoveContextItem(itemId, 1)}
                              disabled={idx === contextMenuItems.length - 1}
                              title="Move down"
                            >
                              <span className="material-symbols-rounded">keyboard_arrow_down</span>
                            </button>
                            <button
                              className="btn-icon-sm remove"
                              onClick={() => handleToggleContextItem(itemId)}
                              title="Remove"
                            >
                              <span className="material-symbols-rounded">remove_circle_outline</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Available items to add */}
                  {availableItems.length > 0 && (
                    <>
                      <h4 style={{ marginTop: '16px' }}>Available Items</h4>
                      <div className="context-items-list available">
                        {availableItems.map(item => (
                          <div key={item.id} className="context-item available">
                            <div className="context-item-icon">
                              {item.isSvg ? (
                                <CustomIcon name={item.icon} size={18} />
                              ) : item.isLetter ? (
                                <span className="letter-icon">{item.icon}</span>
                              ) : (
                                <span className="material-symbols-rounded">{item.icon}</span>
                              )}
                            </div>
                            <span className="context-item-label">
                              {item.parentLabel && <span className="context-item-parent">{item.parentLabel} › </span>}
                              {item.label}
                            </span>
                            <div className="context-item-actions">
                              <button
                                className="btn-icon-sm add"
                                onClick={() => handleToggleContextItem(item.id)}
                                title="Add"
                              >
                                <span className="material-symbols-rounded">add_circle_outline</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Favourites Panel */}
            {activePanel === 'favourites' && (
              <div key="favourites" className={`panel-content-section ${isPanelOpening ? 'panel-opening' : ''}`}>
                <div className="theme-section">
                  <h4>Favourites Nav Menu</h4>
                  <p className="theme-hint">
                    Build a customised sidebar shortcut list that appears under Home.
                    Add items, group them with sections, and break them up with dividers.
                  </p>

                  <div className="favourites-actions" style={{ display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap', marginBottom: 'var(--spacing-md)' }}>
                    <button
                      type="button"
                      className="btn-slideout"
                      onClick={handleCopyFromContextMenu}
                      title="Replace favourites with the current context menu configuration"
                    >
                      <div className="icon-wrapper">
                        <span className="material-symbols-rounded">content_copy</span>
                      </div>
                      <span className="btn-label">Match Context Menu</span>
                    </button>
                    <button
                      type="button"
                      className="btn-slideout"
                      onClick={handleAddFavouriteDivider}
                    >
                      <div className="icon-wrapper">
                        <span className="material-symbols-rounded">horizontal_rule</span>
                      </div>
                      <span className="btn-label">Add Divider</span>
                    </button>
                    {favouritesItems.length > 0 && (
                      <button
                        type="button"
                        className="btn-slideout"
                        onClick={handleClearFavourites}
                      >
                        <div className="icon-wrapper">
                          <span className="material-symbols-rounded">delete_sweep</span>
                        </div>
                        <span className="btn-label">Clear All</span>
                      </button>
                    )}
                  </div>

                  <div className="favourites-section-add" style={{ display: 'flex', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
                    <input
                      type="text"
                      value={favSectionDraft}
                      onChange={(e) => setFavSectionDraft(e.target.value)}
                      placeholder="Section label (e.g. Pricing)"
                      style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--border-secondary)', borderRadius: 'var(--radius-md)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddFavouriteSection(); } }}
                    />
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleAddFavouriteSection}
                      disabled={!favSectionDraft.trim()}
                    >
                      <span className="material-symbols-rounded" style={{ fontSize: 18, marginRight: 4 }}>add</span>
                      Add Section
                    </button>
                  </div>

                  <div className="favourites-submenu-add" style={{ display: 'flex', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
                    <input
                      type="text"
                      value={favSubmenuDraft.label}
                      onChange={(e) => setFavSubmenuDraft(d => ({ ...d, label: e.target.value }))}
                      placeholder="Submenu label (e.g. Tools)"
                      style={{ flex: 2, padding: '6px 10px', border: '1px solid var(--border-secondary)', borderRadius: 'var(--radius-md)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddFavouriteSubmenu(); } }}
                    />
                    <input
                      type="text"
                      value={favSubmenuDraft.icon}
                      onChange={(e) => setFavSubmenuDraft(d => ({ ...d, icon: e.target.value }))}
                      placeholder="icon (material symbol)"
                      style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--border-secondary)', borderRadius: 'var(--radius-md)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddFavouriteSubmenu(); } }}
                    />
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleAddFavouriteSubmenu}
                      disabled={!favSubmenuDraft.label.trim()}
                    >
                      <span className="material-symbols-rounded" style={{ fontSize: 18, marginRight: 4 }}>create_new_folder</span>
                      Add Submenu
                    </button>
                  </div>

                  {/* Current favourites list */}
                  <div className="context-items-list">
                    {favouritesItems.length === 0 && (
                      <p style={{ color: 'var(--text-muted)', padding: 'var(--spacing-md)', textAlign: 'center' }}>
                        No favourites yet — add items from the list below, or copy your context menu above.
                      </p>
                    )}
                    {favouritesItems.map((entry, idx) => {
                      // SUBMENU row: renders inline editor with children when expanded
                      if (entry.type === 'submenu') {
                        const isOpen = favExpandedSubmenus.has(entry.id);
                        const childSectionDraft = favChildSectionDrafts[entry.id] || '';
                        return (
                          <React.Fragment key={idx}>
                            <div className={`context-item active fav-submenu-row ${isOpen ? 'expanded' : ''}`}>
                              <div className="context-item-icon">
                                <span className="material-symbols-rounded">{entry.icon || 'folder'}</span>
                              </div>
                              <span className="context-item-label">
                                <strong>{entry.label || 'Submenu'}</strong>
                                <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: '0.85em' }}>
                                  ({entry.children?.length || 0} item{entry.children?.length === 1 ? '' : 's'})
                                </span>
                              </span>
                              <div className="context-item-actions">
                                <button
                                  className="btn-icon-sm"
                                  onClick={() => toggleFavSubmenuExpanded(entry.id)}
                                  title={isOpen ? 'Collapse editor' : 'Expand editor'}
                                >
                                  <span className="material-symbols-rounded" style={{ transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }}>expand_more</span>
                                </button>
                                <button
                                  className="btn-icon-sm"
                                  onClick={() => handleMoveFavourite(idx, -1)}
                                  disabled={idx === 0}
                                  title="Move up"
                                >
                                  <span className="material-symbols-rounded">keyboard_arrow_up</span>
                                </button>
                                <button
                                  className="btn-icon-sm"
                                  onClick={() => handleMoveFavourite(idx, 1)}
                                  disabled={idx === favouritesItems.length - 1}
                                  title="Move down"
                                >
                                  <span className="material-symbols-rounded">keyboard_arrow_down</span>
                                </button>
                                <button
                                  className="btn-icon-sm remove"
                                  onClick={() => handleRemoveFavourite(idx)}
                                  title="Remove submenu"
                                >
                                  <span className="material-symbols-rounded">remove_circle_outline</span>
                                </button>
                              </div>
                            </div>

                            {isOpen && (
                              <div className="fav-submenu-editor">
                                <div className="fav-submenu-meta" style={{ display: 'flex', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
                                  <input
                                    type="text"
                                    value={entry.label || ''}
                                    onChange={(e) => handleUpdateSubmenuMeta(idx, 'label', e.target.value)}
                                    placeholder="Label"
                                    style={{ flex: 2, padding: '6px 10px', border: '1px solid var(--border-secondary)', borderRadius: 'var(--radius-md)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                                  />
                                  <input
                                    type="text"
                                    value={entry.icon || ''}
                                    onChange={(e) => handleUpdateSubmenuMeta(idx, 'icon', e.target.value)}
                                    placeholder="icon"
                                    style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--border-secondary)', borderRadius: 'var(--radius-md)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                                  />
                                </div>

                                {/* Children list */}
                                <div className="context-items-list" style={{ marginLeft: 20 }}>
                                  {(entry.children || []).length === 0 && (
                                    <p style={{ color: 'var(--text-muted)', padding: 'var(--spacing-sm)', fontSize: '0.85em', fontStyle: 'italic' }}>
                                      Empty — add items, a section, or a divider below.
                                    </p>
                                  )}
                                  {(entry.children || []).map((child, cIdx) => {
                                    let cIcon, cLabel;
                                    if (child.type === 'divider') {
                                      cIcon = <span className="material-symbols-rounded">horizontal_rule</span>;
                                      cLabel = <span className="context-item-label" style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>— Divider —</span>;
                                    } else if (child.type === 'section') {
                                      cIcon = <span className="material-symbols-rounded">title</span>;
                                      cLabel = <span className="context-item-label" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', fontSize: '0.85em' }}>{child.label}</span>;
                                    } else {
                                      const item = menuItemMap[child.id];
                                      if (!item) {
                                        cIcon = <span className="material-symbols-rounded">help</span>;
                                        cLabel = <span className="context-item-label" style={{ color: 'var(--color-error)' }}>Unknown ({child.id})</span>;
                                      } else {
                                        cIcon = item.isSvg ? <CustomIcon name={item.icon} size={18} /> : item.isLetter ? <span className="letter-icon">{item.icon}</span> : <span className="material-symbols-rounded">{item.icon}</span>;
                                        cLabel = (
                                          <span className="context-item-label">
                                            {item.parentLabel && <span className="context-item-parent">{item.parentLabel} › </span>}
                                            {item.label}
                                          </span>
                                        );
                                      }
                                    }
                                    return (
                                      <div key={cIdx} className="context-item active">
                                        <div className="context-item-icon">{cIcon}</div>
                                        {cLabel}
                                        <div className="context-item-actions">
                                          <button
                                            className="btn-icon-sm"
                                            onClick={() => handleMoveChild(idx, cIdx, -1)}
                                            disabled={cIdx === 0}
                                            title="Move up"
                                          >
                                            <span className="material-symbols-rounded">keyboard_arrow_up</span>
                                          </button>
                                          <button
                                            className="btn-icon-sm"
                                            onClick={() => handleMoveChild(idx, cIdx, 1)}
                                            disabled={cIdx === (entry.children.length - 1)}
                                            title="Move down"
                                          >
                                            <span className="material-symbols-rounded">keyboard_arrow_down</span>
                                          </button>
                                          <button
                                            className="btn-icon-sm remove"
                                            onClick={() => handleRemoveChild(idx, cIdx)}
                                            title="Remove"
                                          >
                                            <span className="material-symbols-rounded">remove_circle_outline</span>
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Controls to add to this submenu */}
                                <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center', marginTop: 'var(--spacing-sm)', marginLeft: 20, flexWrap: 'wrap' }}>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => handleAddChildDivider(idx)}
                                    style={{ fontSize: '0.85em', padding: '4px 10px' }}
                                  >
                                    <span className="material-symbols-rounded" style={{ fontSize: 16, marginRight: 2 }}>horizontal_rule</span>
                                    Divider
                                  </button>
                                  <input
                                    type="text"
                                    value={childSectionDraft}
                                    onChange={(e) => setFavChildSectionDrafts(prev => ({ ...prev, [entry.id]: e.target.value }))}
                                    placeholder="Section label"
                                    style={{ flex: 1, minWidth: 140, padding: '5px 10px', border: '1px solid var(--border-secondary)', borderRadius: 'var(--radius-md)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85em' }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddChildSection(idx, childSectionDraft);
                                        setFavChildSectionDrafts(prev => ({ ...prev, [entry.id]: '' }));
                                      }
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => {
                                      handleAddChildSection(idx, childSectionDraft);
                                      setFavChildSectionDrafts(prev => ({ ...prev, [entry.id]: '' }));
                                    }}
                                    disabled={!childSectionDraft.trim()}
                                    style={{ fontSize: '0.85em', padding: '4px 10px' }}
                                  >
                                    <span className="material-symbols-rounded" style={{ fontSize: 16, marginRight: 2 }}>title</span>
                                    Section
                                  </button>
                                </div>

                                {/* Quick-add items from the available menu */}
                                <details className="fav-child-add-items" style={{ marginTop: 'var(--spacing-sm)', marginLeft: 20 }}>
                                  <summary style={{ cursor: 'pointer', fontSize: '0.85em', color: 'var(--text-secondary)' }}>
                                    Add menu items to this submenu…
                                  </summary>
                                  <div className="context-items-list available" style={{ marginTop: 'var(--spacing-xs)' }}>
                                    {contextMenuCandidates.map(item => (
                                      <div key={item.id} className="context-item available">
                                        <div className="context-item-icon">
                                          {item.isSvg ? <CustomIcon name={item.icon} size={18} /> : item.isLetter ? <span className="letter-icon">{item.icon}</span> : <span className="material-symbols-rounded">{item.icon}</span>}
                                        </div>
                                        <span className="context-item-label">
                                          {item.parentLabel && <span className="context-item-parent">{item.parentLabel} › </span>}
                                          {item.label}
                                        </span>
                                        <div className="context-item-actions">
                                          <button
                                            className="btn-icon-sm add"
                                            onClick={() => handleAddChildItem(idx, item.id)}
                                            title="Add to submenu"
                                          >
                                            <span className="material-symbols-rounded">add_circle_outline</span>
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              </div>
                            )}
                          </React.Fragment>
                        );
                      }

                      // Non-submenu rows (item / section / divider)
                      let iconNode, labelNode;
                      if (entry.type === 'divider') {
                        iconNode = <span className="material-symbols-rounded">horizontal_rule</span>;
                        labelNode = <span className="context-item-label" style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>— Divider —</span>;
                      } else if (entry.type === 'section') {
                        iconNode = <span className="material-symbols-rounded">title</span>;
                        labelNode = (
                          <span className="context-item-label" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', fontSize: '0.85em' }}>
                            {entry.label}
                          </span>
                        );
                      } else {
                        const item = menuItemMap[entry.id];
                        if (!item) {
                          iconNode = <span className="material-symbols-rounded">help</span>;
                          labelNode = <span className="context-item-label" style={{ color: 'var(--color-error)' }}>Unknown item ({entry.id})</span>;
                        } else {
                          iconNode = item.isSvg ? (
                            <CustomIcon name={item.icon} size={18} />
                          ) : item.isLetter ? (
                            <span className="letter-icon">{item.icon}</span>
                          ) : (
                            <span className="material-symbols-rounded">{item.icon}</span>
                          );
                          labelNode = (
                            <span className="context-item-label">
                              {item.parentLabel && <span className="context-item-parent">{item.parentLabel} › </span>}
                              {item.label}
                            </span>
                          );
                        }
                      }
                      return (
                        <div key={idx} className="context-item active">
                          <div className="context-item-icon">{iconNode}</div>
                          {labelNode}
                          <div className="context-item-actions">
                            <button
                              className="btn-icon-sm"
                              onClick={() => handleMoveFavourite(idx, -1)}
                              disabled={idx === 0}
                              title="Move up"
                            >
                              <span className="material-symbols-rounded">keyboard_arrow_up</span>
                            </button>
                            <button
                              className="btn-icon-sm"
                              onClick={() => handleMoveFavourite(idx, 1)}
                              disabled={idx === favouritesItems.length - 1}
                              title="Move down"
                            >
                              <span className="material-symbols-rounded">keyboard_arrow_down</span>
                            </button>
                            <button
                              className="btn-icon-sm remove"
                              onClick={() => handleRemoveFavourite(idx)}
                              title="Remove"
                            >
                              <span className="material-symbols-rounded">remove_circle_outline</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Available items to add */}
                  <h4 style={{ marginTop: '16px' }}>Available Items</h4>
                  <p className="theme-hint" style={{ fontSize: '0.85em' }}>
                    Adds the item to the bottom of your list — reorder with the arrow controls.
                  </p>
                  <div className="context-items-list available">
                    {contextMenuCandidates.map(item => (
                      <div key={item.id} className="context-item available">
                        <div className="context-item-icon">
                          {item.isSvg ? (
                            <CustomIcon name={item.icon} size={18} />
                          ) : item.isLetter ? (
                            <span className="letter-icon">{item.icon}</span>
                          ) : (
                            <span className="material-symbols-rounded">{item.icon}</span>
                          )}
                        </div>
                        <span className="context-item-label">
                          {item.parentLabel && <span className="context-item-parent">{item.parentLabel} › </span>}
                          {item.label}
                        </span>
                        <div className="context-item-actions">
                          <button
                            className="btn-icon-sm add"
                            onClick={() => handleAddFavouriteItem(item.id)}
                            title="Add to favourites"
                          >
                            <span className="material-symbols-rounded">add_circle_outline</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default UserProfile;
